import { SUBSCRIPTION_PERIODS } from "../constants/Constants";
import { to2DecimalPriceString } from "./NumberFormatter";

const {
    YEAR,
    QUARTER,
    MONTH,
    WEEK,
    SIX_MONTHS,
    TWO_YEARS,
    THREE_YEARS,
    FOUR_YEARS,
    FIVE_YEARS
} = SUBSCRIPTION_PERIODS;

export const DIGITAL_BULLET_COPY = [
    "Unlimited access to Bloomberg.com, Bloomberg mobile and tablet apps",
    "Bloomberg TV live streaming",
    "The Open and Close, subscriber-only newsletters"
];

export const ALL_ACCESS_BULLET_COPY = [
    "full digital access",
    "Bloomberg Businessweek print edition",
    "Access to Bloomberg live events"
];

interface PriceToFormat {
    price: number;
    period: string;
    hasDiscount?: boolean;
    discountLength?: number;
}

function formatPrice({ price, period, hasDiscount, discountLength }: PriceToFormat): string {
    switch (period.toLowerCase()) {
        case YEAR:
        case QUARTER:
        case MONTH:
        case WEEK:
            return `${ to2DecimalPriceString(price) as string }/${ getTimePeriod(period).toLowerCase() }`;
        case SIX_MONTHS:
        case TWO_YEARS:
        case THREE_YEARS:
        case FOUR_YEARS:
        case FIVE_YEARS:
        default:
            return hasDiscount
                ? formatWithDiscount({ price, period, discountLength })
                : formatWithoutDiscount({ price, period });
    }
}

export function formatRecurringPeriod(period: string): string {
    const numberOfPeriods = getNumberOfPeriods(period);
    const timePeriod = getTimePeriod(period).toLowerCase();
    const formattedNumberOfPeriods = numberOfPeriods > 1 ? `${ numberOfPeriods } ` : "";

    return `every ${ formattedNumberOfPeriods }${ timePeriod }${ numberOfPeriods > 1 ? "s" : "" }`;
}

function formatWithoutDiscount({ price, period }: { price: number; period: string }): string {
    return `${ to2DecimalPriceString(price) as string } ${ formatRecurringPeriod(period) }`;
}

function formatWithDiscount(
    { price, period, discountLength }: { price: number; period: string; discountLength?: number }
): string {
    const numberOfPeriods = (discountLength || NaN) * getNumberOfPeriods(period);
    const timePeriod = getTimePeriod(period).toLowerCase();

    return `${ to2DecimalPriceString(price) as string } upfront for ${ numberOfPeriods } ${ timePeriod }${ numberOfPeriods > 1 ? "s" : "" }`;
}

export function getTimePeriod(period: string): string {
    switch (period.toLowerCase()) {
        case YEAR:
        case TWO_YEARS:
        case THREE_YEARS:
        case FOUR_YEARS:
        case FIVE_YEARS:
            return "Year";
        case QUARTER:
            return "Quarter";
        case WEEK:
            return "Week";
        case SIX_MONTHS:
        case MONTH:
            return "Month";
        default:
            return "Renewal";
    }
}

export function getNumberOfPeriods(period: string): number {
    switch (period.toLowerCase()) {
        case SIX_MONTHS:
            return 6;
        case TWO_YEARS:
            return 2;
        case THREE_YEARS:
            return 3;
        case FOUR_YEARS:
            return 4;
        case FIVE_YEARS:
            return 5;
        case YEAR:
        case QUARTER:
        case MONTH:
        case WEEK:
        default:
            return 1;
    }
}

export function getNumberOfPeriodsInMonth(period: string): number {
    switch (period.toLowerCase()) {
        case SIX_MONTHS:
            return 6;
        case TWO_YEARS:
            return 24;
        case THREE_YEARS:
            return 36;
        case FOUR_YEARS:
            return 48;
        case FIVE_YEARS:
            return 60;
        case YEAR:
            return 12;
        case QUARTER:
            return 3;
        case MONTH:
            return 1;
        default:
            throw new Error("period is not supported");
    }
}

function getSurchargePrefix(number: number): string {
    switch (number) {
        case 1:
            return "first";
        case 2:
            return "second";
        case 4:
            return "fourth";
        case 6:
            return "sixth";
        default:
            return number.toString();
    }
}

export function formatIntroPeriod(
    { discountPeriod, discountLength }: { discountPeriod: string; discountLength: number },
    hasSurcharge?: boolean
): string {
    const numberOfPeriods = getNumberOfPeriods(discountPeriod);
    const timePeriod = getTimePeriod(discountPeriod).toLowerCase();

    const introPeriodLength = hasSurcharge
        ? getSurchargePrefix(discountLength * numberOfPeriods)
        : discountLength * numberOfPeriods;

    return `${ introPeriodLength } ${ timePeriod }`;
}

export function formatOfferPrice({
    total,
    totalWithDiscount,
    billingPeriod,
    discountPeriod,
    hasDiscount,
    discountLength
}: SubscriptionInfo): null | string {
    const price = hasDiscount ? totalWithDiscount : total;
    const period = (hasDiscount && discountPeriod) || billingPeriod;

    if (!period) {
        return null;
    }

    return formatPrice({ price, period, hasDiscount, discountLength });
}

export function formatRecurringPrice({ total, billingPeriod }: SubscriptionInfo): null | string {
    if (!billingPeriod) {
        return null;
    }

    return formatPrice({ price: total, period: billingPeriod });
}
